import QForm from 'quasar/src/components/form/QForm.js';import useQuasar from 'quasar/src/composables/use-quasar.js';
import { ComputedRef } from 'vue'
import { ListFilterModel } from '@/views/_shared/search-list/types'

type SubmitFunction<T> = () => Promise<T>
type OnSubmitEvent = () => unknown
type QuasarType = ReturnType<typeof useQuasar>

export enum FormModelCacheTag {
    AGREEMENTS_LIST_FILTERS = 'agreements-list-filters',
    AUTH_LOGS_LIST_FILTERS = 'auth-logs-list-filters',
    EMPLOYEES_LIST_FILTERS = 'employees-list-filters',
    FAILED_LOGIN_ATTEMPTS_LIST_FILTERS = 'failed-login-attempts-list-filters',
    FREE_FORMS_LIST_FILTERS = 'free-forms-list-filters',
    FREE_SUBSCRIPTIONS_LIST_FILTERS = 'free-subscriptions-list-filters',
    INVOICES_LIST_FILTERS = 'invoices-list-filters',
    MEMBERS_LIST_FILTERS = 'members-list-filters',
    ORGANIZATIONS_LIST_FILTERS = 'organizations-list-filters',
    PACKAGES_LIST_FILTERS = 'packages-list-filters',
    SSO_JOIN_RULES_LIST_FILTERS = 'sso-rules-list-filters',
    WHITELISTED_IPS_LIST_FILTERS = 'whitelisted-ips-list-filters'
}

const formModelCache: {[tag: string]: ListFilterModel | string} = {}

const onSuccessFactory = ($q: QuasarType) => (message = 'Success!') => {
    return () => {
        $q.notify({
            color: 'positive',
            message
        })
    }
}

const onValidationErrorFactory = ($q: QuasarType) => (message = 'You need fill correctly all required fields') => {
    return () => {
        $q.notify({
            color: 'negative',
            message
        })
    }
}

const onApiErrorFactory = ($q: QuasarType) => (message = 'An api error occurred') => {
    return () => {
        $q.notify({
            color: 'negative',
            message
        })
    }
}

async function submitForm<T> (
    form: QForm | undefined,
    fn: SubmitFunction<T>,
    onSuccess: OnSubmitEvent | null = null,
    onValidationError: OnSubmitEvent | null = null,
    onApiError: (<K extends Error>(error: K) => void) | null = null
): Promise<T> {
    if (!form) {
        throw Error('validation error')
    }

    const localOnSuccess = onSuccess || onSuccessFactory(form.$q)()
    const localOnValidationError = onValidationError || onValidationErrorFactory(form.$q)()

    if (!await form.validate()) {
        localOnValidationError()
        throw Error('validation error')
    }

    try {
        const result = await fn()
        localOnSuccess()
        return result
    } catch (err) {
        if (onApiError) {
            onApiError(err)
        }
        form.$q.notify({
            color: 'negative',
            message: err?.message || 'system error'
        })
        throw Error('system error')
    }
}

export function storeFormModel (model: ListFilterModel | string, tag: string) {
    formModelCache[tag] = model
}

export function restoreFormModelTo (formModel: ListFilterModel | string, tag: string) {
    if (formModelCache[tag]) {
        const cacheModel = formModelCache[tag]
        if (typeof formModel === 'string' && typeof cacheModel === 'string') {
            formModel = cacheModel
        } else {
            (Object.keys(cacheModel) as (keyof typeof cacheModel)[]).forEach(key => {
                if (typeof formModel === 'object' && key in formModel) {
                    formModel[key] = cacheModel[key]
                }
            })
        }

        delete formModelCache[tag]
    }
}

export function useFormModule () {
    const $q = useQuasar()

    const createOnSuccess = onSuccessFactory($q)
    const createOnValidationError = onValidationErrorFactory($q)
    const createOnApiError = onApiErrorFactory($q)

    return {
        createOnSuccess,
        createOnValidationError,
        createOnApiError,
        submitForm,
        validations: {
            emailRule,
            requiredRule,
            passwordRule,
            repeatPasswordRule,
            requiredOptionGroupRule,
            urlRule
        }
    }
}

// eslint-disable-next-line no-control-regex
const emailRegex = /^(?:[A-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i
export const emailRule = (val: string) => {
    const match = val.match(emailRegex)
    return match === null ? 'Must be valid e-mail' : true
}
export const urlRule = (val: string) => {
    return (() => {
        if (val === '') {
            return false
        }
        try {
            const url = new URL(val)
            return url.protocol === 'https:' && url.hostname !== '' && url.pathname !== '/'
        } catch (err) {
            return false
        }
    })() || 'Must be a valid URL with path'
}
export const requiredRule = (val: string) => !!val || 'Field is required'

export const passwordRule = (val: string) => (String(val).length > 5) || 'Password must contain minimum 5 characters'
export const repeatPasswordRule = (password: ComputedRef<string | undefined>) => (val: string) => (val === password.value) || 'Passwords differs from each other'
export const requiredOptionGroupRule = (val: string[]) => val.length > 0 || 'Field is required'
